import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ShieldCheck, Leaf, Clock, Building2, ChevronDown, ChevronUp, ArrowLeft, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/918882183014?text=Hi%2C%20I%20want%20to%20book%20commercial%20water%20tank%20cleaning%20in%20Gurugram";

const faqs = [
  { q: "Do you provide a compliance certificate after cleaning?", a: "Yes. After every commercial cleaning, TankBuddy provides a detailed cleaning and disinfection certificate that meets municipal and health authority requirements. This certificate is valid for audit and compliance purposes." },
  { q: "Can you handle tanks larger than 25,000 litres?", a: "Absolutely. We have industrial-grade equipment capable of cleaning tanks up to 1,00,000+ litres. For very large installations, we deploy multiple teams simultaneously to minimize your downtime." },
  { q: "How do you minimize disruption to our operations?", a: "We offer flexible scheduling including nights, weekends, and holidays. For hotels and hospitals, we work in sections to ensure continuous water supply. Our team coordinates with your facility manager for zero-disruption cleaning." },
  { q: "What safety standards do your technicians follow?", a: "Our technicians are trained in confined space operations, hold valid safety certifications, and follow OSHA-equivalent protocols. We carry full insurance and provide safety documentation for your records." },
  { q: "Do you offer annual maintenance contracts (AMC)?", a: "Yes. We offer quarterly and half-yearly AMC plans with priority scheduling, discounted rates (up to 20% off), dedicated account manager, and compliance documentation maintained on your behalf." },
];

const clients = ["Hotels & Resorts", "Corporate Offices", "Shopping Malls", "RWA Societies", "Hospitals & Clinics", "Schools & Colleges", "Factories & Warehouses", "Government Buildings"];

const steps = [
  { title: "Site Survey & Planning", desc: "Detailed assessment of tank infrastructure, capacity, access points, and scheduling to minimize operational disruption." },
  { title: "Industrial Dewatering", desc: "High-capacity pump systems drain tanks rapidly. Tanker disposal arranged for contaminated water per regulations." },
  { title: "Industrial-Grade Jet Cleaning", desc: "200+ bar pressure commercial jet systems with rotating nozzles clean massive surface areas efficiently." },
  { title: "Multi-Stage Disinfection & Certification", desc: "Chemical and UV disinfection, water quality testing, and issuance of compliance certificate." },
];

function FAQ({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors">
        <span className="font-medium text-navy text-sm pr-4">{faq.q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-sky flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-sky flex-shrink-0" />}
      </button>
      {open && <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed">{faq.a}</div>}
    </div>
  );
}

export default function CommercialTankCleaning() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Commercial Water Tank Cleaning in Gurugram | TankBuddy</title>
        <meta name="description" content="Commercial water tank cleaning in Gurugram for hotels, offices, malls, RWAs, hospitals. Industrial-grade equipment, compliance certificate provided. Starting ₹4,999. Book TankBuddy!" />
        <meta name="keywords" content="commercial tank cleaning Gurugram, industrial tank cleaning Gurgaon, RWA tank cleaning, hotel water tank cleaning" />
        <link rel="canonical" href="https://www.tankbuddy.in/commercial-water-tank-cleaning-gurugram" />
        <meta property="og:title" content="Commercial Water Tank Cleaning in Gurugram | TankBuddy" />
        <meta property="og:description" content="Commercial water tank cleaning in Gurugram. Industrial-grade equipment, compliance certificate. Starting ₹4,999." />
        <meta property="og:url" content="https://www.tankbuddy.in/commercial-water-tank-cleaning-gurugram" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Commercial Water Tank Cleaning in Gurugram",
            "provider": {
              "@type": "LocalBusiness",
              "name": "TankBuddy",
              "telephone": "+91-8882183014",
              "url": "https://www.tankbuddy.in",
              "address": {"@type": "PostalAddress", "addressLocality": "Gurugram", "addressRegion": "Haryana", "addressCountry": "IN"}
            },
            "areaServed": "Gurugram",
            "description": "Industrial-grade commercial water tank cleaning for hotels, offices, malls, RWAs, and hospitals in Gurugram. Compliance certificate provided.",
            "offers": {"@type": "Offer", "price": "4999", "priceCurrency": "INR", "priceValidUntil": "2026-12-31"}
          }
        `}</script>
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Commercial Water Tank Cleaning Services in Gurugram
          </h1>

          <div className="flex flex-wrap gap-4 mb-10">
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><ShieldCheck className="w-4 h-4" /> Certified Technicians</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Leaf className="w-4 h-4" /> Eco-friendly</span>
            <span className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full"><Clock className="w-4 h-4" /> Flexible Scheduling</span>
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-sm font-medium px-3 py-1.5 rounded-full"><FileCheck className="w-4 h-4" /> Compliance Certificate</span>
          </div>

          <div className="prose prose-slate max-w-none mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Who Is This Service For?</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              TankBuddy's commercial water tank cleaning service is designed for businesses and organizations that manage large water storage infrastructure. Whether you're a hotel maintaining guest safety standards, a corporate office ensuring employee wellness, or an RWA responsible for hundreds of families — our industrial-grade cleaning meets the highest hygiene standards demanded by commercial operations.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {clients.map(c => (
                <div key={c} className="flex items-center gap-2 bg-surface border border-slate-200 rounded-lg p-3">
                  <Building2 className="w-4 h-4 text-sky flex-shrink-0" />
                  <span className="text-xs font-medium text-navy">{c}</span>
                </div>
              ))}
            </div>

            <h2 className="font-heading font-bold text-2xl text-navy mb-4">Industrial-Grade Cleaning Process</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Commercial tanks require significantly more powerful equipment and expertise than residential cleaning. Our fleet includes high-capacity submersible pumps (5000 LPH+), industrial jet machines operating at 200+ bar pressure, robotic inspection cameras, and multi-stage chemical disinfection systems. We handle tanks from 5,000 litres to over 1,00,000 litres across single and multi-level configurations.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Every commercial job includes a compliance certificate documenting the cleaning process, chemicals used, before/after water quality readings, and technician certifications. This documentation satisfies municipal health inspectors, hotel star-rating auditors, and ISO compliance requirements.
            </p>
          </div>

          <div className="bg-surface border border-slate-200 rounded-xl p-8 mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Our Commercial Cleaning Process</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {steps.map((s, i) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sky font-bold text-sm">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-navy mb-1">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-2xl mb-2">Pricing</h2>
            <p className="text-slate-300 mb-4">Commercial water tank cleaning in Gurugram</p>
            <div className="text-4xl font-heading font-extrabold mb-1">Starting ₹4,999</div>
            <p className="text-slate-400 text-sm mb-6">For tanks up to 25,000 litres. Includes compliance certificate, multi-stage disinfection, and water quality report.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp</a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+918882183014"><Phone className="w-4 h-4 mr-2" /> Call +91 88821 83014</a>
              </Button>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FAQ key={i} faq={faq} />)}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
